/**
 * Commission Rate Calculator
 * Calculates recommended commission rates based on CA HT, CAPEX carrier, and enseigne
 */

// Standard commission rate brackets (for most enseignes)
const COMMISSION_BRACKETS = [
  { max: 350000, ccd: 0.15, client: 0.18, range: '0 € - 350 000 €' },
  { max: 500000, ccd: 0.18, client: 0.20, range: '350 001 € - 500 000 €' },
  { max: 600000, ccd: 0.19, client: 0.21, range: '500 001 € - 600 000 €' },
  { max: 700000, ccd: 0.20, client: 0.22, range: '600 001 € - 700 000 €' },
  { max: 800000, ccd: 0.21, client: 0.23, range: '700 001 € - 800 000 €' },
  { max: 1000000, ccd: 0.22, client: 0.24, range: '800 001 € - 1 000 000 €' },
  { max: Infinity, ccd: 0.23, client: 0.25, range: '> 1 000 001 €' },
];

// Système U specific commission brackets
// Note: For Système U, CCD ALWAYS carries the CAPEX (never the client)
const SYSTEME_U_COMMISSION_BRACKETS = [
  { max: 200000, rate: 0.12, range: '0 € - 200 000 €' },
  { max: 300000, rate: 0.17, range: '200 001 € - 300 000 €' },
  { max: 400000, rate: 0.18, range: '300 001 € - 400 000 €' },
  { max: 500000, rate: 0.20, range: '400 001 € - 500 000 €' },
  { max: 600000, rate: 0.21, range: '500 001 € - 600 000 €' },
  { max: 700000, rate: 0.22, range: '600 001 € - 700 000 €' },
  { max: 800000, rate: 0.23, range: '700 001 € - 800 000 €' },
  { max: Infinity, rate: 0.24, range: '> 800 001 €' },
];

/**
 * Check if the enseigne is Système U (case-insensitive, handles variations)
 * @param {string} enseigne - The enseigne name
 * @returns {boolean}
 */
export const isSystemeU = (enseigne) => {
  if (!enseigne) return false;
  const normalized = enseigne.toLowerCase().trim();
  return normalized.includes('système u') || 
         normalized.includes('systeme u') || 
         normalized.includes('super u') ||
         normalized.includes('hyper u') ||
         normalized.includes('u express') ||
         normalized === 'système u' ||
         normalized === 'systeme u';
};

/**
 * Get recommended commission rate based on CA HT, carrier, and enseigne
 * @param {number} caHt - CA HT annual amount
 * @param {boolean} clientCarriesCapex - Whether CLIENT carries all CAPEX (ignored for Système U)
 * @param {string} enseigne - The enseigne name (optional)
 * @returns {number} Commission rate (0.12 - 0.25)
 */
export const getRecommendedCommissionRate = (caHt, clientCarriesCapex, enseigne = '') => {
  // Special case: Système U has its own grid and CCD always carries CAPEX
  if (isSystemeU(enseigne)) {
    for (const bracket of SYSTEME_U_COMMISSION_BRACKETS) {
      if (caHt <= bracket.max) {
        return bracket.rate;
      }
    }
    return 0.24; // Default max for Système U
  }
  
  // Standard logic for other enseignes
  for (const bracket of COMMISSION_BRACKETS) {
    if (caHt <= bracket.max) {
      return clientCarriesCapex ? bracket.client : bracket.ccd;
    }
  }
  return clientCarriesCapex ? 0.25 : 0.23;
};

/**
 * Get the CA bracket label for a given CA HT and enseigne
 * @param {number} caHt - CA HT annual amount
 * @param {string} enseigne - The enseigne name (optional)
 * @returns {string} Bracket range label
 */
export const getCaBracket = (caHt, enseigne = '') => {
  // Use Système U brackets if applicable
  if (isSystemeU(enseigne)) {
    for (const bracket of SYSTEME_U_COMMISSION_BRACKETS) {
      if (caHt <= bracket.max) {
        return bracket.range;
      }
    }
    return '> 800 001 €';
  }
  
  // Standard brackets
  for (const bracket of COMMISSION_BRACKETS) {
    if (caHt <= bracket.max) {
      return bracket.range;
    }
  }
  return '> 1 000 001 €';
};

/**
 * Check if client carries all CAPEX
 * @param {object} capexInvestment - CAPEX investment object with corner_investor, vitrine_investor, inox_investor
 * @returns {boolean}
 */
export const isClientCapexCarrier = (capexInvestment) => {
  return (
    capexInvestment.corner_investor === 'CLIENT' &&
    capexInvestment.vitrine_investor === 'CLIENT' &&
    capexInvestment.inox_investor === 'CLIENT'
  );
};

/**
 * Get all commission brackets for display
 * @param {string} enseigne - The enseigne name (optional)
 * @returns {Array} Array of bracket objects
 */
export const getCommissionBrackets = (enseigne = '') => {
  if (isSystemeU(enseigne)) {
    return SYSTEME_U_COMMISSION_BRACKETS;
  }
  return COMMISSION_BRACKETS;
};

/**
 * Get the CAPEX carrier for a given enseigne
 * For Système U, CCD always carries the CAPEX
 * @param {string} enseigne - The enseigne name
 * @param {object} capexInvestment - CAPEX investment object (used for non-Système U)
 * @returns {string} 'CCD' or 'CLIENT'
 */
export const getCapexCarrierForEnseigne = (enseigne, capexInvestment) => {
  // Système U: CCD always carries CAPEX
  if (isSystemeU(enseigne)) {
    return 'CCD';
  }
  // For other enseignes, check the investment breakdown
  return isClientCapexCarrier(capexInvestment) ? 'CLIENT' : 'CCD';
};

export default {
  getRecommendedCommissionRate,
  getCaBracket,
  isClientCapexCarrier,
  getCommissionBrackets,
  isSystemeU,
  getCapexCarrierForEnseigne,
};
